import dayjs from "dayjs";
import { CalendarIcon } from "lucide-react";
import { useId, useState } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { FieldShell } from "./FieldShell";
import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

const YMD = "YYYY-MM-DD";

/** `YYYY-MM-DD` -> local Date, or undefined when blank / invalid. */
export const parseYmd = (
  value: string | null | undefined
): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
};

export const formatYmd = (d: Date): string => dayjs(d).format(YMD);

type Props<T extends FieldValues> = BaseFieldProps<T> & {
  placeholder?: string;
  /** Inclusive `YYYY-MM-DD` bounds. */
  min?: string;
  max?: string;
  /** Year range for the month/year dropdowns. */
  fromYear?: number;
  toYear?: number;
  /** Shows a Clear action once a date is chosen. Defaults to `!required`. */
  clearable?: boolean;
  /** Display format for the chosen date. */
  displayFormat?: string;
  /** Runs after the form value changes (for dependent fields). */
  onValueChange?: (value: string) => void;
};

/**
 * Calendar popover bound to a `YYYY-MM-DD` string field. Month and year
 * dropdowns make far-off dates (birthdays, expirations) quick to reach.
 */
export function DateField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  placeholder = "Pick a date",
  min,
  max,
  fromYear = 1900,
  toYear = new Date().getFullYear() + 30,
  clearable,
  displayFormat = "MMM D, YYYY",
  onValueChange
}: Props<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  const [open, setOpen] = useState(false);
  const canClear = clearable ?? !required;
  const minDate = parseYmd(min);
  const maxDate = parseYmd(max);
  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : [])
  ];

  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => {
        const selected = parseYmd(field.value);
        const set = (v: string) => {
          field.onChange(v);
          onValueChange?.(v);
        };
        return (
          <FieldShell
            id={id}
            label={label}
            required={required}
            description={description}
            error={fieldState.error}
            disabled={disabled}
            className={className}
          >
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  id={id}
                  ref={field.ref}
                  type="button"
                  variant="outline"
                  disabled={disabled}
                  aria-invalid={fieldState.invalid || undefined}
                  onBlur={field.onBlur}
                  className={cn(
                    "h-8 w-full justify-between px-2.5 text-left font-normal",
                    !selected && "text-muted-foreground",
                    fieldState.invalid &&
                      "border-destructive ring-3 ring-destructive/20"
                  )}
                >
                  <span className="truncate">
                    {selected
                      ? dayjs(selected).format(displayFormat)
                      : placeholder}
                  </span>
                  <CalendarIcon className="size-4 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  startMonth={new Date(fromYear, 0)}
                  endMonth={new Date(toYear, 11)}
                  selected={selected}
                  defaultMonth={selected ?? maxDate ?? undefined}
                  disabled={disabledDays.length ? disabledDays : undefined}
                  onSelect={(d) => {
                    if (d) set(formatYmd(d));
                    setOpen(false);
                  }}
                />
                <div className="flex items-center justify-between gap-2 border-t border-border px-2 py-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      set(formatYmd(new Date()));
                      setOpen(false);
                    }}
                  >
                    Today
                  </Button>
                  {canClear && selected ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground"
                      onClick={() => {
                        set("");
                        setOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
              </PopoverContent>
            </Popover>
          </FieldShell>
        );
      }}
    />
  );
}
